import {
  getClubDetail as getClubDetailApi,
  clubRoles as getClubRolesApi,
  getClubs as getClubsApi,
  getMyClubs as getMyClubsApi,
} from '@/services/club'
import {
  Club,
  GetClubDetailResponse,
  GetClubRolesResponse,
  GetClubsResponse,
  GetMyClubResponse,
  MyClub,
  PayloadGetClubRoles,
  PayloadGetClubs,
  ResultGetClubs,
} from '@/types'
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { flatten } from 'lodash'

interface ClubStore {
  isLoading: boolean
  isLoadingMyClubs: boolean
  isLoadingClubs: boolean
  myClubs: MyClub[]
  resClubs: ResultGetClubs
  club: Club | null
  permissions: string[] | null
}

export const GetMyClubsPrefix = '@Club/GetMyClubs'
export const GetClubsPrefix = '@Club/GetClubs'
export const GetClubDetailPrefix = '@Club/GetClubDetail'
export const GetClubRolesPrefix = '@Club/GetClubRoles'

export const getMyClubs = createAsyncThunk(GetMyClubsPrefix, async () => {
  return await getMyClubsApi()
})

export const getClubs = createAsyncThunk(GetClubsPrefix, async (payload: PayloadGetClubs) => {
  const { offset } = payload
  const response = await getClubsApi(payload)
  return { response, offset }
})

export const getClubDetail = createAsyncThunk(GetClubDetailPrefix, async (clubId: string) => {
  return await getClubDetailApi(clubId)
})

export const getClubRoles = createAsyncThunk(GetClubRolesPrefix, async (payload: PayloadGetClubRoles) => {
  return await getClubRolesApi(payload)
})

const initialState: ClubStore = {
  isLoading: false,
  isLoadingMyClubs: false,
  isLoadingClubs: false,
  myClubs: [],
  resClubs: {
    offset: 0,
    limit: 10,
    total: 0,
    hits: [],
    totalValueRelation: null,
    hasMore: false,
    hasPrevious: false,
    empty: false,
  },
  club: null,
  permissions: null,
}

const clubSlice = createSlice({
  name: 'CLUB_SLICE',
  initialState,
  reducers: {
    resetClub: (state) => {
      Object.assign(state, initialState)
    },
    updateClub: (state, action) => {
      state.club = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getMyClubs.pending, (state) => {
      state.isLoadingMyClubs = true
    })
    builder.addCase(getMyClubs.fulfilled, (state, action: PayloadAction<GetMyClubResponse>) => {
      state.myClubs = action.payload.results
      state.isLoadingMyClubs = false
    })
    builder.addCase(getMyClubs.rejected, (state) => {
      state.isLoadingMyClubs = false
    })

    builder.addCase(getClubs.pending, (state) => {
      state.isLoadingClubs = true
    })
    builder.addCase(
      getClubs.fulfilled,
      (state, action: PayloadAction<{ response: GetClubsResponse; offset: number }>) => {
        if (action.payload.offset === 0) {
          state.resClubs = action.payload.response.result
        }
        if (action.payload.offset !== 0) {
          const updatedResClubs = {
            ...state.resClubs,
            empty: action.payload.response.result.empty,
            hasMore: action.payload.response.result.hasMore,
            hasPrevious: action.payload.response.result.hasPrevious,
            hits: [
              ...new Map(
                [...state.resClubs.hits, ...action.payload.response.result.hits].map((item) => [item.clubId, item])
              ).values(),
            ],
            limit: action.payload.response.result.limit,
            offset: action.payload.offset,
            total: action.payload.response.result.total,
            totalValueRelation: action.payload.response.result.totalValueRelation,
          }
          state.resClubs = updatedResClubs
        }

        state.isLoadingClubs = false
      }
    )
    builder.addCase(getClubs.rejected, (state) => {
      state.isLoadingClubs = false
    })

    builder.addCase(getClubDetail.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(getClubDetail.fulfilled, (state, action: PayloadAction<GetClubDetailResponse>) => {
      state.club = action.payload.result
      state.isLoading = false
    })
    builder.addCase(getClubDetail.rejected, (state) => {
      state.isLoading = false
    })

    builder.addCase(getClubRoles.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(getClubRoles.fulfilled, (state, action: PayloadAction<GetClubRolesResponse>) => {
      const { result } = action.payload
      if (result) {
        const permissionsArray = [
          ...flatten(
            Object.entries(result?.permissions).map(([key, values]) => values.map((value) => `${key}::${value}`))
          ),
          result?.role,
        ]
        state.permissions = permissionsArray
      }
      if (!result) state.permissions = []
      state.isLoading = false
    })
    builder.addCase(getClubRoles.rejected, (state) => {
      state.isLoading = false
    })
  },
})

const { reducer, actions } = clubSlice

export const { resetClub, updateClub } = actions

export default reducer
